
import React from "react";
import { Form } from "react-bootstrap";
import { Bot, Paperclip, Upload, X } from "lucide-react";
import SelectGroup from "./SelectGroup";
import {
  CERTIFICATIONS,
  COMPACT_CHOICE_FIELDS,
  DOCUMENT_TYPE_OPTIONS,
} from "../../config/rfqConfig";
import type { DocumentItem, Field, OptionType, RequestType } from "../../types/rfq";

interface RfqFieldRendererProps {
  field: Field;
  request: RequestType;
  errors: Record<string, string>;
  countryOptions: OptionType[];
  citySuggestions: string[];
  activeCertifField: string | null;
  setActiveCertifField: (value: string | null) => void;
  onChange: (name: string, value: any) => void;
  onFileUpload: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onRemoveFile: (index: number) => void;
  onUpdateDocumentType: (index: number, type: string) => void;
  onToastError: (message: string) => void;
}

export default function RfqFieldRenderer({
  field,
  request,
  errors,
  countryOptions,
  citySuggestions,
  activeCertifField,
  setActiveCertifField,
  onChange,
  onFileUpload,
  onRemoveFile,
  onUpdateDocumentType,
  onToastError,
}: RfqFieldRendererProps) {
  const safeArray = (v: any) => (Array.isArray(v) ? v : []);
  const hasError = (fieldId: string) => !!errors[fieldId];
  const getFieldError = (fieldId: string) => errors[fieldId];

  const renderError = (fieldId: string) =>
    hasError(fieldId) ? <div className="field-error">{getFieldError(fieldId)}</div> : null;

  const value =
    field.type === "checkbox" || field.type === "tags" || field.type === "certifications"
      ? request[field.id] || []
      : request[field.id] || "";

  switch (field.type) {
    case "SelectGroup":
      return (
        <>
          <div className={`select-wrapper-fixed ${hasError(field.id) ? "has-error" : ""}`}>
            <SelectGroup
              controlId={field.id}
              label={field.label}
              icon="list"
              options={field.id === "country" ? countryOptions : field.options || []}
              valueType={
                value
                  ? {
                      value,
                      label:
                        (field.id === "country" ? countryOptions : field.options || []).find(
                          (opt: any) => String(opt.value) === String(value),
                        )?.label || String(value),
                    }
                  : null
              }
              onChange={(opt) => onChange(field.id, opt?.value || "")}
              isSearchable={field.searchable ?? true}
              isClearable
            />
          </div>
          {renderError(field.id)}
        </>
      );

    case "textarea":
      return (
        <>
          {field.id === "description" && (
            <div className="ai-help-text">
              <Bot size={12} className="me-1" />
              Utilisée pour le pré-remplissage AI
            </div>
          )}
          <Form.Control
            as="textarea"
            rows={3}
            className={`input-modern ${hasError(field.id) ? "input-error" : ""}`}
            value={value}
            placeholder={field.placeholder || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
          />
          {renderError(field.id)}
        </>
      );

    case "radio":
      return (
        <>
          <div
            className={`choice-grid ${
              COMPACT_CHOICE_FIELDS.includes(field.id) ? "choice-grid-compact" : ""
            }`}
          >
            {field.options?.map((opt: string) => (
              <label
                key={opt}
                className={`choice-card ${value === opt ? "active radio" : ""} ${
                  hasError(field.id) ? "choice-error" : ""
                }`}
              >
                <Form.Check
                  type="radio"
                  label={opt}
                  checked={value === opt}
                  onChange={() => onChange(field.id, opt)}
                />
              </label>
            ))}
          </div>
          {renderError(field.id)}
        </>
      );

    case "checkbox": {
      const selectedValues = Array.isArray(value) ? value : [];
      const gridClass =
        field.id === "logistics"
          ? "choice-grid logistics-grid"
          : `choice-grid ${COMPACT_CHOICE_FIELDS.includes(field.id) ? "choice-grid-compact" : ""}`;

      return (
        <>
          <div className={gridClass}>
            {(field.options || []).map((option) => {
              const checked = selectedValues.includes(option);

              return (
                <label
                  key={option}
                  className={`choice-card ${checked ? "active checkbox" : ""}`}
                >
                  <Form.Check
                    type="checkbox"
                    label={option}
                    checked={checked}
                    onChange={(e) => {
                      const next = e.target.checked
                        ? [...selectedValues, option]
                        : selectedValues.filter((v) => v !== option);

                      onChange(field.id, next);
                    }}
                  />
                </label>
              );
            })}
          </div>
          {renderError(field.id)}
        </>
      );
    }

    case "tags":
      return (
        <>
          <Form.Control
            list={field.id === "cities" && request.country ? "cities-suggestions" : undefined}
            className={`input-modern ${hasError(field.id) ? "input-error" : ""}`}
            placeholder={
              field.id === "cities"
                ? request.country
                  ? "Tape une ville puis Entrée"
                  : "Choisissez d’abord un pays"
                : "Tape une valeur puis Entrée"
            }
            disabled={field.id === "cities" && !request.country}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              if (e.key !== "Enter") return;

              e.preventDefault();
              const val = e.currentTarget.value.trim();
              if (!val) return;

              const allowed =
                field.id !== "cities" ||
                citySuggestions.some((city) => city.toLowerCase() === val.toLowerCase());

              if (!allowed) {
                onToastError("Choisissez une ville du pays sélectionné.");
                return;
              }

              const current = safeArray(value);
              if (!current.includes(val)) {
                onChange(field.id, [...current, val]);
              }

              e.currentTarget.value = "";
            }}
          />

          {field.id === "cities" && request.country && (
            <datalist id="cities-suggestions">
              {citySuggestions.map((city) => (
                <option key={city} value={city} />
              ))}
            </datalist>
          )}

          <div className="tag-list mt-2">
            {safeArray(value).map((tag: string, i: number) => (
              <span
                key={i}
                className="tag-chip"
                onClick={() =>
                  onChange(
                    field.id,
                    safeArray(value).filter((t: string) => t !== tag),
                  )
                }
              >
                {tag} <X size={12} />
              </span>
            ))}
          </div>

          {field.id === "cities" && request.country && citySuggestions.length > 0 && (
            <div className="hint-text mt-1">{citySuggestions.length} villes disponibles pour ce pays</div>
          )}

          {renderError(field.id)}
        </>
      );

    case "file":
      return (
        <>
          <div
            className={`dropzone-pro ${hasError(field.id) ? "dropzone-error" : ""}`}
            onClick={() => document.getElementById("rfqFileInput")?.click()}
          >
            <Upload size={22} />
            <p className="mt-2 mb-1 fw-semibold">Ajoutez vos documents</p>
            <small>Plan, CDC, spécification, image, photo...</small>
          </div>

          <input id="rfqFileInput" type="file" multiple hidden onChange={onFileUpload} />

          {request.documents.length > 0 && (
            <div className="document-grid mt-3">
              {request.documents.map((doc: DocumentItem, i: number) => (
                <div key={`${doc.file.name}-${i}`} className="document-card compact">
                  <div className="document-card-head">
                    <div className="d-flex align-items-center gap-2 flex-grow-1">
                      <div className="document-icon-box">
                        <Paperclip size={13} />
                      </div>
                      <div className="document-meta">
                        <div className="file-name">{doc.file.name}</div>
                        <div className="file-size">{(doc.file.size / 1024 / 1024).toFixed(2)} MB</div>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="btn-file-remove"
                      onClick={() => onRemoveFile(i)}
                    >
                      <X size={14} />
                    </button>
                  </div>

                  <Form.Select
                    className="input-modern document-type-select"
                    value={doc.type}
                    onChange={(e) => onUpdateDocumentType(i, e.target.value)}
                  >
                    <option value="">Choisir un type</option>
                    {DOCUMENT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </Form.Select>
                </div>
              ))}
            </div>
          )}
          {renderError(field.id)}
        </>
      );

    case "certifications":
      return (
        <>
          <div style={{ position: "relative" }}>
            <Form.Control
              placeholder="Ajouter une certification..."
              className={`input-modern ${hasError(field.id) ? "input-error" : ""}`}
              onFocus={() => setActiveCertifField(field.id)}
              onBlur={() => setTimeout(() => setActiveCertifField(null), 200)}
              onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                if (e.key !== "Enter") return;

                e.preventDefault();
                const val = e.currentTarget.value.trim();
                if (!val) return;

                if (!safeArray(value).includes(val)) {
                  onChange(field.id, [...safeArray(value), val]);
                }

                e.currentTarget.value = "";
              }}
            />

            {activeCertifField === field.id && (
              <div className="certif-dropdown">
                {CERTIFICATIONS.map((cert) => (
                  <div
                    key={cert}
                    className={`certif-item ${safeArray(value).includes(cert) ? "active" : ""}`}
                    onMouseDown={() => {
                      const current = safeArray(value);
                      const updated = current.includes(cert)
                        ? current.filter((c: string) => c !== cert)
                        : [...current, cert];

                      onChange(field.id, updated);
                    }}
                  >
                    {cert}
                  </div>
                ))}
              </div>
            )}

            <div className="tag-list mt-2">
              {safeArray(value).map((tag: string, i: number) => (
                <span
                  className="tag-chip"
                  key={i}
                  onClick={() =>
                    onChange(
                      field.id,
                      safeArray(value).filter((t: string) => t !== tag),
                    )
                  }
                >
                  {tag} <X size={12} />
                </span>
              ))}
            </div>
          </div>
          {renderError(field.id)}
        </>
      );

    default:
      return (
        <>
          <Form.Control
            className={`input-modern ${hasError(field.id) ? "input-error" : ""}`}
            type={field.type}
            value={value}
            min={field.type === "number" ? 0 : undefined}
            placeholder={field.placeholder || ""}
            onChange={(e) => onChange(field.id, e.target.value)}
          />
          {renderError(field.id)}
        </>
      );
  }
}
